import React, { useEffect, useState } from 'react';
import { Table, Divider, Tag } from 'rsuite';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import TablePagination from '@/components/TablePagination';
import { BaseErrorModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import { PointAccountTransaction, PointAccountTransactionReason, PointAccountTransactionReasonList } from '@/models/point-account.model';
import { getPagedPointAccountTransactions } from '@/services/point-account.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;

const reasonColor: Record<number, string> = {
  [PointAccountTransactionReason.Fund]: 'green',
  [PointAccountTransactionReason.Grant]: 'blue',
  [PointAccountTransactionReason.Adjust]: 'orange',
};

interface Props {
  pointAccountId: number;
  reloadKey?: number;
}

const PointAccountTransactionList: React.FC<Props> = ({ pointAccountId, reloadKey }) => {
  const [filterReq, setFilterReq] = useState<PaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<PointAccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filterReq, pointAccountId, reloadKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getPagedPointAccountTransactions(pointAccountId, filterReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching account ledger: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContent>
      <Table height={500} data={items} loading={loading}>
        <Column flexGrow={0.5}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={0.8} align="right">
          <HeaderCell>Amount</HeaderCell>
          <Cell>
            {(r: PointAccountTransaction) => (
              <span style={{ color: r.amount >= 0 ? '#2db85d' : '#e55', fontWeight: 600 }}>
                {r.amount > 0 ? '+' : ''}{r.amount}
              </span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.8} align="center">
          <HeaderCell>Reason</HeaderCell>
          <Cell>
            {(r: PointAccountTransaction) => (
              <Tag color={reasonColor[r.reason] as any}>
                {PointAccountTransactionReasonList.find(x => x.value == r.reason)?.label ?? r.reason}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={2.5} fullText>
          <HeaderCell>Note</HeaderCell>
          <Cell dataKey="note" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>User Point Tx</HeaderCell>
          <Cell>{(r: PointAccountTransaction) => r.userPointTransactionId ?? '-'}</Cell>
        </Column>
        <Column flexGrow={0.7}>
          <HeaderCell>Created By</HeaderCell>
          <Cell dataKey="createdBy" />
        </Column>
        <Column flexGrow={1.2}>
          <HeaderCell>Created At</HeaderCell>
          <Cell fullText>{(r: PointAccountTransaction) => jsDateToZone(r.createdAt)}</Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
    </PageContent>
  );
};

export default PointAccountTransactionList;
